import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HeaderComponent } from '../../header/header.component';
import { FooterComponent } from '../../footer/footer.component';

interface PricingPlan {
  name: string;
  price: number;
  color: string;
  description: string;
  features: {
    listingDuration: number;
    imagesAllowed: string;
    descriptionLength: string;
    appearsHigherInSearch: string;
    listingInFeaturedSection: string;
    listingInSuggestedItems: string;
    emailNotifications: string;
    freeRelist: string;
  };
  popular: boolean;
  recommended?: boolean;
}

@Component({
  selector: 'app-fees-payments',
  standalone: true,
  imports: [CommonModule, HeaderComponent, FooterComponent],
  templateUrl: './fees-payments.component.html',
  styleUrls: ['./fees-payments.component.css'],
})
export class FeesPaymentsComponent {
  plans: PricingPlan[] = [
    {
      name: 'Free',
      price: 0.0,
      color: 'gray',
      description: 'Perfect for testing the waters and individual sellers',
      features: {
        listingDuration: 7,
        imagesAllowed: 'Up to 7',
        descriptionLength: '300 characters',
        appearsHigherInSearch: 'No',
        listingInFeaturedSection: 'No',
        listingInSuggestedItems: 'No',
        emailNotifications: 'No',
        freeRelist: 'No',
      },
      popular: false,
    },
    {
      name: 'Starter',
      price: 1.99,
      color: 'blue',
      description: 'Ideal for regular sellers and small businesses',
      features: {
        listingDuration: 14,
        imagesAllowed: 'Up to 14',
        descriptionLength: '400 characters',
        appearsHigherInSearch: 'Yes',
        listingInFeaturedSection: 'No',
        listingInSuggestedItems: 'No',
        emailNotifications: 'No',
        freeRelist: 'No',
      },
      popular: false,
    },
    {
      name: 'Pro',
      price: 2.99,
      color: 'purple',
      description: 'Most popular choice for growing businesses',
      features: {
        listingDuration: 21,
        imagesAllowed: 'Up to 21',
        descriptionLength: '500 characters',
        appearsHigherInSearch: 'Yes',
        listingInFeaturedSection: 'Yes',
        listingInSuggestedItems: 'No',
        emailNotifications: 'No',
        freeRelist: 'No',
      },
      popular: true,
    },
    {
      name: 'Elite',
      price: 3.99,
      color: 'red',
      description: 'Premium solution for maximum visibility and results',
      features: {
        listingDuration: 42,
        imagesAllowed: 'Unlimited',
        descriptionLength: '5000 characters',
        appearsHigherInSearch: 'Yes',
        listingInFeaturedSection: 'Yes',
        listingInSuggestedItems: 'Yes',
        emailNotifications: 'Yes',
        freeRelist: 'Yes (1 Free)',
      },
      popular: false,
    },
  ];

  getPlanCardClasses(plan: PricingPlan): string {
    const baseClasses = 'pricing-card';
    const modifierClasses = [];

    if (plan.popular) {
      modifierClasses.push('popular');
    }

    return [baseClasses, ...modifierClasses].join(' ');
  }

  getPlanHeaderClasses(plan: PricingPlan): string {
    const colorVariants = {
      gray: 'bg-gradient-to-br from-slate-500 to-slate-600',
      blue: 'bg-gradient-to-br from-blue-500 to-blue-600',
      purple: 'bg-gradient-to-br from-purple-500 to-purple-600',
      red: 'bg-gradient-to-br from-red-500 to-red-600',
    };

    const baseClasses = 'plan-header';
    const colorClass =
      colorVariants[plan.color as keyof typeof colorVariants] ||
      colorVariants.gray;

    return `${baseClasses} ${colorClass}`;
  }

  getPlanButtonClasses(plan: PricingPlan): string {
    const colorVariants = {
      gray: 'bg-slate-600 hover:bg-slate-700 focus:ring-slate-500',
      blue: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
      purple: 'bg-purple-600 hover:bg-purple-700 focus:ring-purple-500',
      red: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
    };

    const baseClasses =
      'plan-button text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:-translate-y-1 focus:outline-none focus:ring-4 focus:ring-opacity-50';
    const colorClass =
      colorVariants[plan.color as keyof typeof colorVariants] ||
      colorVariants.gray;

    return `${baseClasses} ${colorClass}`;
  }

  getFeatureStatusClasses(status: string): string {
    const baseClasses = 'feature-value font-medium';

    if (status === 'Yes' || status.includes('Yes') || status === 'Unlimited') {
      return `${baseClasses} feature-yes text-emerald-600`;
    }

    return `${baseClasses} text-slate-500`;
  }

  isFeatureIncluded(status: string): boolean {
    return status === 'Yes' || status.includes('Yes') || status === 'Unlimited';
  }

  formatPrice(price: number): string {
    if (price === 0) {
      return 'Free';
    }
    return `$${price.toFixed(2)}`;
  }

  getCtaText(plan: PricingPlan): string {
    if (plan.price === 0) {
      return 'Start Free Trial';
    }

    if (plan.popular) {
      return 'Choose Business Plan';
    }

    return `Select ${plan.name}`;
  }

  trackPlanView(plan: PricingPlan): void {}

  ngOnInit(): void {}
}
